import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Upload, Camera, ArrowRight, Sparkles, ShoppingBag, TrendingDown, Check } from 'lucide-react';
import Button from 'shared/components/ui/Button';
import PublicWrapper from 'shared/components/layout/PublicWrapper';
import AITryOnDemo from 'shared/components/VirtualTryOn/AITryOnDemo';
import { URLS } from 'configs';
import './VirtualTryOnPage.scss';

const VirtualTryOnPage = () => {
  const { t } = useTranslation();

  return (
    <PublicWrapper>
      <Helmet>
        <title>AI Virtual Try-On & Look Builder | Find & Buy Complete Looks | Likiee</title>
        <meta
          name='description'
          content='Upload your photo to virtually try on clothes AND let AI find all look elements online you can purchase. See styles on YOU before buying. AI-powered fashion discovery and virtual fitting room.'
        />
        <meta
          name='keywords'
          content='virtual try-on, AI fashion, try on clothes online, look builder, find outfit online, virtual fitting room, AI try-on, shop the look'
        />
      </Helmet>
      <main className='virtual-tryon-page'>
        {/* Hero Section */}
        <section className='virtual-tryon-page__hero'>
          <div className='container'>
            <div className='virtual-tryon-page__hero-content'>
              <h1 className='virtual-tryon-page__hero-title'>
                {t('tryon_hero_title_1', 'Try On Styles &')}
                <br />
                <span className='gradient-text'>{t('tryon_hero_title_2', 'Find Every Piece to Buy')}</span>
              </h1>
              <p className='virtual-tryon-page__hero-description'>
                {t(
                  'tryon_hero_description',
                  'Upload your photo to see styles on YOU, then our AI finds all look elements online that you can purchase. From inspiration to your wardrobe in minutes.',
                )}
              </p>
              <div className='virtual-tryon-page__hero-actions'>
                <Button
                  className='button-hero'
                  onClick={() => document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Sparkles />
                  {t('tryon_try_demo', 'Try Demo Now')}
                  <ArrowRight />
                </Button>
                <Button variant='outline'>
                  <Camera />
                  {t('tryon_watch_video', 'Watch Video')}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className='virtual-tryon-page__how-it-works'>
          <div className='container'>
            <h2 className='virtual-tryon-page__section-title'>{t('tryon_how_it_works', 'How It Works')}</h2>
            <div className='virtual-tryon-page__steps'>
              {[
                {
                  icon: Upload,
                  title: t('tryon_step1_title', 'Upload Your Photo'),
                  desc: t('tryon_step1_desc', 'Take or upload a full-body photo'),
                },
                {
                  icon: Sparkles,
                  title: t('tryon_step2_title', 'Pick a Style'),
                  desc: t('tryon_step2_desc', 'Browse curated looks or upload inspiration'),
                },
                {
                  icon: ShoppingBag,
                  title: t('tryon_step3_title', 'Shop the Look'),
                  desc: t('tryon_step3_desc', 'AI finds every piece online to buy'),
                },
              ].map((step, index) => (
                <div key={step.title} className='virtual-tryon-page__step'>
                  <div className='virtual-tryon-page__step-number'>{index + 1}</div>
                  <div className='virtual-tryon-page__step-icon'>
                    <step.icon />
                  </div>
                  <h3 className='virtual-tryon-page__step-title'>{step.title}</h3>
                  <p className='virtual-tryon-page__step-desc'>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Demo */}
        <section id='demo-section' className='virtual-tryon-page__demo'>
          <div className='container'>
            <h2 className='virtual-tryon-page__section-title'>{t('tryon_try_it', 'Try It Yourself')}</h2>
            <AITryOnDemo showTitle={false} />
          </div>
        </section>

        {/* Benefits */}
        <section className='virtual-tryon-page__benefits'>
          <div className='container'>
            <h2 className='virtual-tryon-page__section-title'>{t('tryon_why_use', 'Why Use Virtual Try-On?')}</h2>
            <div className='virtual-tryon-page__benefits-grid'>
              {[
                {
                  icon: Check,
                  title: t('tryon_benefit1_title', 'See Before You Buy'),
                  desc: t('tryon_benefit1_desc', 'Visualize how styles look on YOUR body'),
                },
                {
                  icon: TrendingDown,
                  title: t('tryon_benefit2_title', 'Reduce Returns'),
                  desc: t('tryon_benefit2_desc', 'Make confident purchases, save money'),
                },
                {
                  icon: ShoppingBag,
                  title: t('tryon_benefit3_title', 'Shop Complete Looks'),
                  desc: t('tryon_benefit3_desc', 'AI finds every piece to recreate the style'),
                },
              ].map(benefit => (
                <div key={benefit.title} className='virtual-tryon-page__benefit'>
                  <div className='virtual-tryon-page__benefit-icon'>
                    <benefit.icon />
                  </div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className='virtual-tryon-page__cta'>
          <div className='container'>
            <div className='virtual-tryon-page__cta-content'>
              <h2>{t('tryon_cta_title', 'Ready to Transform Your Shopping?')}</h2>
              <p>{t('tryon_cta_description', 'Join thousands using AI to shop smarter and look better')}</p>
              <a href={URLS.CONTACT} className='virtual-tryon-page__cta-link'>
                <Button className='button-hero'>
                  <Sparkles />
                  {t('contact_us', 'Contact us')}
                  <ArrowRight />
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </PublicWrapper>
  );
};
export default VirtualTryOnPage;
